/**
 * Browser entry point.
 *
 * The replica is opened and loaded into memory *before* React renders, so the first paint
 * already has the user's projects. Nothing on the boot path waits for the network.
 */

import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import './index.css';
import { App } from './App.js';
import { ClerkSession } from './auth/ClerkSession.js';
import { DevSession } from './auth/DevSession.js';
import { useSession } from './auth/session.js';
import { StoreProvider } from './data/context.js';
import { Persistence } from './data/persistence.js';
import { AppStore } from './data/store.js';
import { SyncEngine } from './data/sync.js';
import { devToolsEnabled } from './dev/enabled.js';
import { TimeTravel } from './dev/TimeTravel.js';
import { readRuntimeConfig } from './runtime-config.js';

/** Keeps the replication engine pointed at the current session and running. */
function Replication({ engine, children }: { engine: SyncEngine; children: ReactNode }): ReactNode {
  const { getToken } = useSession();

  useEffect(() => {
    engine.setTokenProvider(getToken);
  }, [engine, getToken]);

  useEffect(() => {
    engine.start();
    return () => engine.stop();
  }, [engine]);

  return children;
}

/** Renders a start-up failure that the app cannot recover from on its own. */
function bootFailure(message: string): void {
  const root = document.getElementById('root');
  if (root === null) return;
  root.innerHTML = '';
  const paragraph = document.createElement('p');
  paragraph.style.cssText = 'margin:3rem auto;max-width:34rem;padding:0 1.25rem;'
    + 'font:1rem/1.45 system-ui,sans-serif';
  paragraph.textContent = message;
  root.append(paragraph);
}

/** Opens the replica, wires the providers, and mounts the app. */
async function boot(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) throw new Error('Missing #root element');

  let persistence: Persistence;
  try {
    persistence = await Persistence.open();
  } catch (cause) {
    bootFailure(
      'This browser would not open local storage, which Anwesenheit needs to work offline. '
      + 'Private browsing windows often block it. '
      + (cause instanceof Error ? cause.message : ''),
    );
    return;
  }

  const store = new AppStore(persistence);
  await store.hydrate();

  const config = readRuntimeConfig();
  const engine = new SyncEngine(store, persistence, async () => null);

  const content = (
    <Replication engine={engine}>
      <App />
      {devToolsEnabled(config.devAuth) ? <TimeTravel /> : null}
    </Replication>
  );

  const session = config.devAuth || config.clerkPublishableKey === ''
    ? <DevSession>{content}</DevSession>
    : <ClerkSession publishableKey={config.clerkPublishableKey}>{content}</ClerkSession>;

  createRoot(container).render(
    <StrictMode>
      <StoreProvider value={store}>{session}</StoreProvider>
    </StrictMode>,
  );
}

void boot();
